import { useEffect, useState } from "react";
import { Radio, Volume2 } from "lucide-react";
import useNowPlaying from "@/hooks/useNowPlaying";

/**
 * /embed — the tiny brutalist "Now Spinning" widget designed to be
 * dropped into anyone's site via <iframe>. Auto-updates every 15s off
 * the same useNowPlaying hook the main site uses.
 *
 * URL params:
 *   ?theme=light | dark (default)
 *   ?compact=1 → smaller 80px layout
 *   ?transparent=1 → skip the background so it inherits the host site
 *   ?accent=RRGGBB (hex without #) → override the red accent color
 *
 * All navigation from the widget opens the parent DRR site in the top
 * window (target="_top"/_blank) so embedders never trap their visitors
 * inside the iframe.
 */
export default function Embed() {
  const np = useNowPlaying();
  const [params] = useState(() =>
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams(),
  );

  const theme = (params.get("theme") || "dark").toLowerCase();
  const compact = params.get("compact") === "1";
  const transparent = params.get("transparent") === "1";
  const accentRaw = (params.get("accent") || "").replace(/[^0-9a-f]/gi, "").slice(0, 6);
  const accent = accentRaw.length === 6 ? `#${accentRaw}` : "#dc2626";

  const isLight = theme === "light";
  const bg = transparent
    ? "transparent"
    : isLight
      ? "#fafafa"
      : "#0a0a0a";
  const fg = isLight ? "#0a0a0a" : "#fafafa";
  const muted = isLight ? "#525252" : "#a1a1aa";
  const border = isLight ? "#e4e4e7" : "#27272a";

  const hasTrack = !!(np?.ok && np?.title);
  const trackLabel = hasTrack
    ? np.artist
      ? `${np.artist} — ${np.title}`
      : np.title
    : "//_SILENCE BETWEEN SPINS";

  // Signal the parent frame that we're loaded — some embedders auto-resize
  // their iframe based on this.
  useEffect(() => {
    if (window.top !== window.self) {
      try {
        window.parent.postMessage(
          { type: "drr:widget:ready", compact },
          "*",
        );
      } catch (e) {
        /* silent */
      }
    }
  }, [compact]);

  return (
    <div
      className="drr-embed-root"
      data-testid="drr-embed-widget"
      style={{
        background: bg,
        color: fg,
        border: transparent ? "none" : `2px solid ${border}`,
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        padding: compact ? "10px 12px" : "14px 16px",
        display: "flex",
        alignItems: "center",
        gap: compact ? 10 : 14,
        minHeight: compact ? 60 : 84,
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      {/* Left: brutalist red slash + LIVE dot */}
      <div
        aria-hidden
        style={{
          width: 6,
          alignSelf: "stretch",
          background: accent,
          flexShrink: 0,
        }}
      />

      {/* Middle: eyebrow + track */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 9,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: accent,
            fontWeight: 700,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              background: accent,
              borderRadius: "50%",
              boxShadow: `0 0 0 3px ${accent}22`,
              display: "inline-block",
              animation: "drr-embed-pulse 1.6s ease-in-out infinite",
            }}
          />
          //_NOW_SPINNING · LIVE
        </div>
        <div
          title={trackLabel}
          style={{
            fontFamily:
              '"Archivo Black", "Impact", system-ui, sans-serif',
            fontSize: compact ? 14 : 17,
            lineHeight: 1.15,
            letterSpacing: "-0.01em",
            color: fg,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {trackLabel}
        </div>
        {!compact && (
          <div
            style={{
              fontSize: 9,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              color: muted,
              fontWeight: 700,
            }}
          >
            <span style={{ color: accent }}>DIGITAL REVOLUTION RADIO</span>{" "}
            · HARD_ROCK · HEAVY_METAL · 24/7
          </div>
        )}
      </div>

      {/* Right: TUNE IN button */}
      <a
        href="https://digitalrevolutionradio.com"
        target="_blank"
        rel="noopener noreferrer"
        title="Open Digital Revolution Radio in a new tab"
        data-testid="drr-embed-tune-in"
        style={{
          flexShrink: 0,
          background: accent,
          color: isLight ? "#fafafa" : "#0a0a0a",
          textDecoration: "none",
          fontFamily:
            '"Archivo Black", "Impact", system-ui, sans-serif',
          fontSize: compact ? 10 : 11,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          padding: compact ? "6px 10px" : "8px 14px",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          border: `2px solid ${accent}`,
          transition: "background 120ms ease, color 120ms ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = isLight ? "#0a0a0a" : "#fafafa";
          e.currentTarget.style.color = accent;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = accent;
          e.currentTarget.style.color = isLight ? "#fafafa" : "#0a0a0a";
        }}
      >
        {compact ? (
          <Volume2 size={12} strokeWidth={2.5} />
        ) : (
          <Radio size={13} strokeWidth={2.5} />
        )}
        TUNE IN
      </a>

      <style>{`
        @keyframes drr-embed-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(1.25); }
        }
        html, body, #root {
          margin: 0;
          padding: 0;
          background: ${transparent ? "transparent" : bg};
        }
        body {
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
